import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { getApiUrl } from "@utils/baseUrl";
import ReactCountryFlag from "react-country-flag";
import Select from "react-select";
import LoaderImg from "@assets/images/loader.gif";
import { useSelector } from "react-redux";
import { globalCountryDetailsDictionary } from "@core/global/global-country-details";
import { useDebounce } from "@utils/utils";
import LoadingSkeleton from "@shared/LoadingSkeleton";

const WORLD_ENUM = {
  data_source: "universal",
  country_code: "world",
};

const Glossary = () => {
  const [loading, setLoading] = useState(true);
  const [isCardDataLoading, setIsCardDataLoading] = useState(true);
  const [dropdownData, setDropdownData] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [cardData, setCardData] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredCardData, setFilteredCardData] = useState([]);
  const [uniqueAlphabetsList, setUniqueAlphabetsList] = useState([]);
  const userData = useSelector((state) => state.userDetails.userData);
  const globalPlanCheck = userData?.planIds?.find((planId) => planId?.navigate === "search" && planId?.productId === globalCountryDetailsDictionary?.global_plan?.product_id);
  const isGlobalPlan = globalPlanCheck?.navigate === "search" ? true : false;
  const dataSourceDetails = useSelector((state) => state?.shipmentDetails?.searchParameters?.dataSource);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const customStyles = {
    control: (provided) => ({
      ...provided,
      border: "none",
      boxShadow: "none",
    }),
    menu: (provided) => ({
      ...provided,
      width: "180px",
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "2px 4px",
      margin: "0",
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      padding: "0",
      margin: "0",
    }),
    singleValue: (provided) => ({
      ...provided,
      fontWeight: "500",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: "0",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? "#EDE9FE" : "white",
      color: "black",
      "&:hover": {
        backgroundColor: "#f2f4f7",
      },
    }),
  };

  const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  };

  const highlightText = (text, highlight) => {
    if (!highlight) return text;

    const escapedHighlight = escapeRegExp(highlight);
    const parts = text.split(new RegExp(`(${escapedHighlight})`, "gi"));
    return parts.map((part, index) =>
      part.toLowerCase() === highlight.toLowerCase() ? (
        <span key={index} className="tw-bg-yellow-200 tw-font-semibold">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  useEffect(() => {
    setIsCardDataLoading(true);
    handleSearch();
    setTimeout(() => {
      setIsCardDataLoading(false);
    }, 500);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    getDropdownOptions();
  }, []);

  const generateUppercaseAlphabets = () => {
    const alphabets = [];
    for (let i = 65; i <= 90; i++) {
      alphabets.push(`${String.fromCharCode(i)}`);
    }
    return alphabets;
  };

  const alphabetsArray = generateUppercaseAlphabets();

  const getCardData = async (countryCode) => {
    try {
      setIsCardDataLoading(true);
      const { data: { data: glossaryData = [], unique_alphabet_list: uniqueAlphabets = [] } = {} } = await axios.get(getApiUrl("intelGlossaryData"), { params: { country: countryCode } });
      setCardData(glossaryData);
      setFilteredCardData(glossaryData);
      setUniqueAlphabetsList(uniqueAlphabets);
    } catch (error) {
      toast.error("Something went wrong. Please try again later !!!");
    } finally {
      setIsCardDataLoading(false);
    }
  };

  const getDropdownOptions = async () => {
    try {
      setLoading(true);
      const { data: { data: countryData = [] } = {} } = await axios.get(getApiUrl("glossaryInformation"), { params: {} });
      if (countryData) {
        setDropdownData(countryData);
        const countryCode = globalCountryDetailsDictionary?.[dataSourceDetails]?.countryCode || "world";
        let country = countryCode === "world" ? WORLD_ENUM : countryData.find((item) => item.country_code === countryCode.toLowerCase());
        if (!country) {
          country = WORLD_ENUM;
        }
        setSelectedCountry({
          value: country.country_code,
          label: (
            <div className="tw-flex tw-items-center tw-capitalize">
              {country.country_code === "world" ? <img src="https://res.cloudinary.com/drzta9shk/image/upload/v1681887388/intel/icons/alldata_icon_ednqbi.svg" alt="Universal" className="tw-mr-2 tw-w-5 tw-h-5" /> : <ReactCountryFlag countryCode={countryCode} className="tw-mr-2" svg />}
              {country.data_source}
            </div>
          ),
        });
        getCardData(country.country_code);
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again later !!!");
    } finally {
      setLoading(false);
    }
  };

  const countryOptions = useMemo(
    () =>
      dropdownData.map((country) => ({
        value: country.country_code,
        label: (
          <div className="tw-flex tw-items-center tw-capitalize">
            {country.country_code === "world" ? <img src="https://res.cloudinary.com/drzta9shk/image/upload/v1681887388/intel/icons/alldata_icon_ednqbi.svg" alt="Universal" className="tw-mr-2 tw-w-5 tw-h-5" /> : <ReactCountryFlag countryCode={country.country_code === "world" ? "UN" : country.country_code.toUpperCase()} svg className="tw-mr-2" />}
            {country.data_source}
          </div>
        ),
      })),
    [dropdownData]
  );

  const handleCountryChange = (selectedOption) => {
    setSelectedCountry(selectedOption);
    setSearchTerm("");
    getCardData(selectedOption.value);
  };

  const handleSearch = () => {
    if (cardData) {
      if (!debouncedSearchTerm) {
        setFilteredCardData(cardData);
        const filteredAlphabets = cardData.map((item) => item.alphabet);
        setUniqueAlphabetsList(filteredAlphabets);
        return;
      }

      const filtered = cardData
        .map((card) => ({
          ...card,
          field_data: card.field_data.filter((field) => field.key.toLowerCase().includes(searchTerm.trim().toLowerCase())),
        }))
        .filter((card) => card.field_data.length > 0);
      setFilteredCardData(filtered);
      const filteredAlphabets = filtered.map((item) => item.alphabet);
      setUniqueAlphabetsList(filteredAlphabets);
    }
  };

  const handleAlphabetClick = (alphabet, filteredCardData) => {
    if (uniqueAlphabetsList.includes(alphabet)) {
      const filteredCardDataIndex = filteredCardData.findIndex((item) => item.alphabet === alphabet);
      if (filteredCardDataIndex !== -1) {
        document.getElementById(alphabet).scrollIntoView({
          behavior: "smooth",
        });
      }
    }
  };

  return (
    <div className="tw-px-5">
      <div>
        <div className="tw-flex tw-flex-col tw-items-center tw-mt-3">
          <div className="tw-text-base tw-font-medium tw-text-gray-900">Trademo Glossary Section</div>
          <div className="tw-text-xs tw-text-gray-500 tw-mt-2">This glossary explains common terms found in across the Trademo platform</div>
          <div className="tw-flex tw-items-center tw-border tw-border-gray-200 tw-rounded-full tw-px-3 tw-py-2 tw-mt-2 tw-w-3/5">
            {!isGlobalPlan ? (
              <>
                <div>
                  <Select value={selectedCountry} onChange={(option) => handleCountryChange(option)} options={countryOptions} placeholder="Select a country" isSearchable={false} className="tw-w-[180px]" styles={customStyles} />
                </div>
                <div className="tw-text-gray-200 tw-mx-2">|</div>
              </>
            ) : (
              <></>
            )}
            <input value={searchTerm} type="text" placeholder="Type here to search" className="tw-flex-grow tw-text-sm tw-text-gray-700 tw-bg-transparent tw-border-none tw-outline-none tw-mr-16" onChange={(e) => setSearchTerm(e.target.value)} />
          </div>
        </div>
        <div className="tw-flex mt-3">
          {alphabetsArray.map((alphabet, index) => (
            <div key={index} className="tw-flex tw-items-center" onClick={() => handleAlphabetClick(alphabet, filteredCardData)}>
              <span className={`tw-text-gray-700 tw-text-sm tw-font-medium tw-underline ${uniqueAlphabetsList.includes(alphabet) ? "tw-cursor-pointer" : "tw-opacity-50 tw-cursor-not-allowed"}`}>{alphabet}</span>
              {index < alphabetsArray.length - 1 && <span className="tw-text-gray-200 tw-mx-1">|</span>}
            </div>
          ))}
        </div>
      </div>
      <div className="tw-mt-4">
        {loading ? (
          <div className="loader-container">
            <div className="loader-block">
              <img src={LoaderImg} alt="description of gif" />
            </div>
          </div>
        ) : isCardDataLoading ? (
          <LoadingSkeleton lines={8} lineHeight="35px" width="100%" shape="rectangle" />
        ) : (
          <div className="tw-h-[calc(100vh-248px)] tw-overflow-auto tw--ml-3">
            {cardData && filteredCardData.length === 0 ? (
              <div className="tw-h-full tw-flex tw-items-center tw-justify-center">
                <div className="tw-flex tw-flex-col">
                  <img src="https://res.cloudinary.com/drzta9shk/image/upload/v1670225921/intel/icons/no_result_illustration_avz4aw.svg" alt="No Data Found" />
                  <div className="tw-mt-4">No results found for your search criteria.</div>
                </div>
              </div>
            ) : (
              filteredCardData.map((card, index) => (
                <div key={index} id={card.alphabet} className="tw-p-2">
                  <div className="tw-rounded-lg tw-bg-white tw-border tw-border-solid tw-shadow-glossary tw-border-gray-200 tw-py-4 tw-px-4">
                    <div className="tw-flex tw-justify-between tw-cursor-pointer">
                      <div className="tw-font-semibold">{card.alphabet}</div>
                    </div>

                    {card?.field_data.length > 0 &&
                      card.field_data.map((field, index) => (
                        <div key={index}>
                          <div className="tw-text-xs tw-font-semibold tw-text-gray-900 tw-mt-4">{highlightText(field.key, searchTerm.trim())}</div>
                          <div className="tw-mt-1 tw-text-xs tw-text-gray-700">{field.description}</div>
                        </div>
                      ))}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Glossary;
